/**
 * UniverseFactCard: one opened fact, read in full beside the orbit.
 *
 * On the desk it replaces the list in the page rail. It is a region, not a modal:
 * the orbit stays usable beside it, so focus is moved to the title on open and
 * never trapped. On a phone it is a bottom sheet, which IS a modal (focus trap,
 * Escape and a scrim tap close it, focus returns afterwards).
 */
import { Fragment, useCallback, useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Dialog, Modal, ModalOverlay } from "react-aria-components";
import { ArrowRight, ChevronLeft, ChevronRight, GitCompareArrows, Trash2, X } from "lucide-react";
import type { StageFact } from "../data/universeLayout";
import { formatDayMonthYear, isNearIdenticalName, stripFactPrefix } from "../data/universeText";
import { useCoShellPortal } from "../CoShellPortalContext";
import { UNIVERSE_FACT_CARD_ID } from "./UniverseOutlineList";

const DAY = 86_400_000;

export type FactCardGroup = {
  key: string;
  label: string;
  /** Every fact filed in the group, in list order. */
  facts: StageFact[];
};

export type FactCardNeighbour = {
  fact: StageFact;
  /** Where the neighbour is filed, e.g. "Mission & vision". */
  groupLabel: string;
};

type Props = {
  fact: StageFact;
  /** "Brand", "Mission & vision", or "Competitors", "Cookpad", "What they sell". */
  crumbs: string[];
  /** The opened fact's primary group. */
  group: FactCardGroup | null;
  /** Other groups of the same subject this fact is also filed under. */
  alsoFiledUnder: string[];
  /** Up to three similarity neighbours, strongest first. */
  closeTo: FactCardNeighbour[];
  compact: boolean;
  onStep: (dir: 1 | -1) => void;
  onClose: () => void;
  onOpenFact: (id: string) => void;
  /** Omitted for a Guest: no agent to ask. */
  onAsk?: () => void;
  /** Omitted for a Guest. */
  onReconcile?: (other: StageFact) => void;
  /** Omitted without edit rights. Opens the existing confirm modal. */
  onDelete?: () => void;
  deleting?: boolean;
};

function isTextField(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  return el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName);
}

function datesLine(fact: StageFact): string {
  const learned = formatDayMonthYear(fact.learnedAt);
  const updatedMs = Date.parse(fact.updatedAt) - Date.parse(fact.learnedAt);
  const updated = Number.isFinite(updatedMs) && updatedMs >= DAY ? formatDayMonthYear(fact.updatedAt) : "";
  return [learned ? `Learned ${learned}` : "", updated ? `Updated ${updated}` : ""].filter(Boolean).join(" · ");
}

function CardBody({
  fact,
  crumbs,
  group,
  alsoFiledUnder,
  closeTo,
  compact,
  onStep,
  onClose,
  onOpenFact,
  onAsk,
  onReconcile,
  onDelete,
  deleting,
}: Props) {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const title = stripFactPrefix(fact.label, fact.concept);
  const members = group?.facts ?? [];
  const pos = members.findIndex((f) => f.id === fact.id);
  const n = members.length;
  const canStep = n > 1;
  const groupLabel = group?.label ?? fact.concept;

  // Focus the title on open, and again whenever the fact changes under a focus
  // that no longer exists (opening a neighbour replaces the button just used).
  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    // A new fact reads from its top: opening one from the group list at the
    // bottom of the card must not leave its title scrolled out of view.
    const scroller = body.closest<HTMLElement>(".co-universe-rail, .co-fact-sheet-dialog");
    if (scroller && scroller.scrollTop > 0) scroller.scrollTop = 0;
    const active = document.activeElement;
    if (active && active !== document.body && body.contains(active)) return;
    titleRef.current?.focus({ preventScroll: true });
  }, [fact.id]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape" && !compact) {
      e.preventDefault();
      onClose();
      return;
    }
    if ((e.key === "ArrowLeft" || e.key === "ArrowRight") && canStep && !isTextField(e.target)) {
      e.preventDefault();
      onStep(e.key === "ArrowRight" ? 1 : -1);
    }
  };

  const meta = compact
    ? [datesLine(fact), pos >= 0 && n ? `${pos + 1} of ${n} in ${groupLabel}` : ""].filter(Boolean).join(" · ")
    : datesLine(fact);

  const stepButtons: ReactNode = (
    <div className="co-fact-card-steps">
      <button type="button" className="co-fact-card-icon" onClick={() => onStep(-1)} disabled={!canStep} aria-label={`Previous fact in ${groupLabel}`}>
        <ChevronLeft size={16} strokeWidth={1.8} aria-hidden />
      </button>
      {!compact ? (
        <span className="co-fact-card-count" aria-live="polite">
          {pos >= 0 ? pos + 1 : 1} of {Math.max(1, n)}
        </span>
      ) : null}
      <button type="button" className="co-fact-card-icon" onClick={() => onStep(1)} disabled={!canStep} aria-label={`Next fact in ${groupLabel}`}>
        <ChevronRight size={16} strokeWidth={1.8} aria-hidden />
      </button>
      <button type="button" className="co-fact-card-icon co-fact-card-close" onClick={onClose} aria-label="Close this fact">
        <X size={16} strokeWidth={1.8} aria-hidden />
      </button>
    </div>
  );

  return (
    <div ref={bodyRef} className="co-fact-card-body" data-compact={compact || undefined} onKeyDown={onKeyDown}>
      <div className="co-fact-card-nav">
        <nav className="co-fact-card-crumbs" aria-label="Where this fact is filed">
          {crumbs.map((c, i) => (
            <Fragment key={`${c}-${i}`}>
              {i > 0 ? <ChevronRight size={12} strokeWidth={2} className="co-fact-card-crumb-sep" aria-hidden /> : null}
              <span>{c}</span>
            </Fragment>
          ))}
        </nav>
        {stepButtons}
      </div>

      <h2 ref={titleRef} tabIndex={-1} className="co-fact-card-title" dir="auto">{title}</h2>
      {fact.desc ? <p className="co-fact-card-reading" dir="auto">{fact.desc}</p> : null}
      {meta ? <p className="co-fact-card-meta">{meta}</p> : null}
      {alsoFiledUnder.length ? (
        <p className="co-fact-card-meta co-fact-card-meta--tight">Also filed under {alsoFiledUnder.join(", ")}</p>
      ) : null}

      {onAsk || onDelete ? (
        <div className="co-fact-card-actions">
          {onAsk ? (
            <button type="button" className="co-universe-cta" onClick={onAsk} disabled={deleting}>
              Ask agents
              <ArrowRight size={14} strokeWidth={2} aria-hidden />
            </button>
          ) : <span />}
          {onDelete ? (
            <button type="button" className="co-fact-card-delete" onClick={onDelete} disabled={deleting}>
              <Trash2 size={14} strokeWidth={1.8} aria-hidden />
              {deleting ? "Deleting" : "Delete"}
            </button>
          ) : null}
        </div>
      ) : null}

      {closeTo.length ? (
        <section className="co-fact-card-section" aria-labelledby="co-fact-card-close-to">
          <h3 id="co-fact-card-close-to" className="co-fact-card-head">
            Close to <span className="co-fact-card-head-count">{closeTo.length}</span>
          </h3>
          <ul className="co-fact-card-near-list">
            {closeTo.map(({ fact: other, groupLabel: otherGroup }) => {
              const near = isNearIdenticalName(fact, other);
              return (
                <li key={other.id} className="co-fact-card-near">
                  <button type="button" className="co-fact-card-near-open" onClick={() => onOpenFact(other.id)}>
                    <span className="co-fact-card-near-top">
                      <span className="co-fact-card-near-name" dir="auto">{stripFactPrefix(other.label, other.concept)}</span>
                      <span className="co-fact-card-near-group">{otherGroup}</span>
                    </span>
                    {other.desc ? <span className="co-fact-card-near-read" dir="auto">{other.desc}</span> : null}
                  </button>
                  {near ? (
                    <div className="co-fact-card-near-foot">
                      <p className="co-fact-card-near-flag">
                        <GitCompareArrows size={14} strokeWidth={1.8} aria-hidden />
                        Near-identical name, different claim.
                      </p>
                      {onReconcile ? (
                        <button
                          type="button"
                          className="co-fact-card-reconcile"
                          aria-label="Ask the agents to reconcile the two"
                          onClick={() => onReconcile(other)}
                        >
                          Reconcile
                          <ArrowRight size={14} strokeWidth={2} aria-hidden />
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      {group && members.length ? (
        <section className="co-fact-card-section" aria-labelledby="co-fact-card-group">
          <h3 id="co-fact-card-group" className="co-fact-card-head">
            {group.label} <span className="co-fact-card-head-count">{members.length}</span>
          </h3>
          <ul className="co-fact-card-group-list">
            {members.map((m) => {
              const current = m.id === fact.id;
              const name = stripFactPrefix(m.label, m.concept);
              return (
                <li key={m.id}>
                  {current ? (
                    <div className="co-fact-card-row" data-current="true" aria-current="true">
                      <span className="co-fact-card-row-mark" aria-hidden />
                      <span className="co-fact-card-row-name" dir="auto">{name}</span>
                      <span className="co-fact-card-row-open">Open</span>
                    </div>
                  ) : (
                    <button type="button" className="co-fact-card-row" onClick={() => onOpenFact(m.id)}>
                      <span className="co-fact-card-row-mark" aria-hidden />
                      <span className="co-fact-card-row-name" dir="auto">{name}</span>
                      <ChevronRight size={14} strokeWidth={1.8} className="co-fact-card-row-chev" aria-hidden />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

/** The sheet's exit (U1, 8 Oct): it sinks 24px as it fades, 240ms ease-in. */
const SHEET_EXIT_MS = 240;

/**
 * A phone sheet that plays its exit before the page lets it go (Universe U1).
 * The page unmounts a sheet the moment it closes, which leaves nothing to
 * animate, so the sheet closes itself first and tells the page once it has sunk.
 * Returns whether it is open and the close to use for every way out.
 */
export function useSheetExit(onClose: () => void): [boolean, () => void] {
  const [open, setOpen] = useState(true);
  const timer = useRef(0);
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const close = useCallback(() => {
    setOpen(false);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => onCloseRef.current(), SHEET_EXIT_MS);
  }, []);
  return [open, close];
}

export default function UniverseFactCard(props: Props) {
  return props.compact ? <UniverseFactSheet {...props} /> : <UniverseFactRegion {...props} />;
}

function UniverseFactRegion(props: Props) {
  const title = stripFactPrefix(props.fact.label, props.fact.concept);
  return (
    <section id={UNIVERSE_FACT_CARD_ID} className="co-fact-card" aria-label={title}>
      <CardBody {...props} />
    </section>
  );
}

function UniverseFactSheet(props: Props) {
  const portalContainer = useCoShellPortal();
  const title = stripFactPrefix(props.fact.label, props.fact.concept);
  const [open, close] = useSheetExit(props.onClose);

  // The shell hands over its portal target one render after it mounts. A sheet
  // opened by a ?fact= link on first paint would otherwise portal to <body>,
  // and React Aria would mark the whole app inert behind it for good.
  if (!portalContainer) return null;

  return (
    <ModalOverlay
      isOpen={open}
      isDismissable
      onOpenChange={(next) => {
        if (!next) close();
      }}
      UNSTABLE_portalContainer={portalContainer ?? undefined}
      className="co-fact-sheet-scrim"
    >
      <Modal className="co-fact-sheet">
        <Dialog id={UNIVERSE_FACT_CARD_ID} aria-label={title} className="co-fact-sheet-dialog">
          <div className="co-fact-sheet-handle" aria-hidden />
          <CardBody {...props} onClose={close} />
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
